/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';

import {
  AppState,
  View,
  Text,
  Platform,
  useWindowDimensions,
} from 'react-native';
import { useTranslation } from 'react-i18next';

import MasonryGrid from './MasonryGrid';

import RecommendationCard from './RecommendationCard';
import ForYouSkeltonLoadingView from './ForYouSkeltonLoadingView';
import AdCardView from './AdCardView/AdCardView';

import SpinnerLoaderView from '@airasia-phoenix/react-native/components/SpinnerLoaderView';

import {
  SpinnerLoaderViewStyle,
  SpinnerLoaderViewSize,
} from '@airasia-phoenix/react-native/components/SpinnerLoaderView/types';

import ButtonView from '@airasia-phoenix/react-native/components/ButtonView';

import { ButtonViewType } from '@airasia-phoenix/react-native/components/ButtonView';
import { styles } from './Recommend.styles';
import { useLogger } from '@airasia-rn/core-logger';
import { useDeviceInfo } from '@airasia-rn/ui/utils';
import { useFlagEnabled } from '@airasia-rn/account';
import { FEATURE_FLAG_HOME_ENABLE_ADS_RENDER } from '../../constants';
import {
  RecommendationEvents,
  QUARTILE_PAGE_SIZE,
  TRACKING_PAGE_SIZE,
  trackRecommendationApiFailure,
  trackRecommendationCardClick,
  trackRecommendationImpression,
  trackRecommendationScrollQuartile,
  trackRecommendationViewBatch,
  RECOMMENDATION_TAB_TITLE_BY_ID,
  type RecommendationTrackingItem,
} from '../../analytics/recommendation';
import mapRecommendationToCardProps from '../../utils/mapRecommendationToCardProps';
import type { RecommendationApiItem } from '../../models/recommendTabResponse';
import { useForYouFeed } from '../../hooks/useForYouFeed';

export interface ForYouFeedRef {
  maybeLoadMoreOnScroll: () => void;
}

const TAB_NAME_FOR_YOU = RECOMMENDATION_TAB_TITLE_BY_ID.for_you;

const ForYouFeed = forwardRef<
  ForYouFeedRef,
  {
    tabName?: string;
    onRecommendationPress?: (params: {
      itemId?: string;
      lob?: string;
      title?: string;
      deepLink?: string;
    }) => void;
    onPressCta?: (params: { deepLink?: string }) => void;
    onShowMorePress?: (params: {
      tabName: string;
      quartile: number;
      maxIndex: number;
    }) => void;
  }
>(
  (
    { tabName = 'for you', onRecommendationPress, onPressCta, onShowMorePress },
    ref,
  ) => {
    const { t } = useTranslation();
    const { logEvent, logError } = useLogger();
    const {
      items,

      loading,

      loadingMore,
      loadMoreError,

      error,
      errorInfo,

      loadMore,

      retry,
      refreshing,
      refresh,
      imageRefreshKey,
      showSeeMore,
    } = useForYouFeed();
    const [imageResumeKey, setImageResumeKey] = useState(0);

    useEffect(() => {
      if (Platform.OS === 'web') return;

      let previousStatus = AppState.currentState;
      const subscription = AppState.addEventListener('change', (status) => {
        if (previousStatus !== 'active' && status === 'active') {
          setImageResumeKey((current) => current + 1);
        }
        previousStatus = status;
      });

      return () => subscription.remove();
    }, []);

    const impressedIdsRef = useRef<Set<string>>(new Set());
    const trackedViewIndexRef = useRef(0);
    const viewItemLastIndexRef = useRef(0);
    const lastApiErrorLabelRef = useRef<string>('');
    const endReachedSentinelRef = useRef<View>(null);
    const isCheckingEndReachedRef = useRef(false);
    const autoLoadInFlightRef = useRef(false);
    const previousLoadMoreErrorRef = useRef(loadMoreError);
    const deviceInfo = useDeviceInfo();
    const windowDimensions = useWindowDimensions();
    const { height: windowHeight, width: windowWidth } =
      Platform.OS === 'web' ? deviceInfo : windowDimensions;
    const mobileCardSize = Math.floor((windowWidth - 16 * 2 - 12) / 2);
    const { enabled: isAdsRenderEnabled } = useFlagEnabled(
      FEATURE_FLAG_HOME_ENABLE_ADS_RENDER,
    );
    const [failedAdIds, setFailedAdIds] = useState<Set<string>>(new Set());

    const handleAdFailed = useCallback((lobItemId: string) => {
      setFailedAdIds((prev) => {
        if (prev.has(lobItemId)) return prev;
        return new Set(prev).add(lobItemId);
      });
    }, []);

    useEffect(() => {
      if (refreshing || (previousLoadMoreErrorRef.current && !loadMoreError)) {
        autoLoadInFlightRef.current = false;
      }

      previousLoadMoreErrorRef.current = loadMoreError;
    }, [loadMoreError, refreshing]);

    const handleRetry = useCallback(() => retry(), [retry]);
    const retryPagination = useCallback(() => {
      void loadMore();
    }, [loadMore]);
    const handleShowMorePress = useCallback(() => {
      const maxIndex = items.length;
      const quartile = Math.min(
        100,
        Math.ceil((maxIndex * 100) / QUARTILE_PAGE_SIZE),
      );

      void loadMore();
      onShowMorePress?.({
        tabName,
        quartile,
        maxIndex,
      });
    }, [items.length, loadMore, onShowMorePress, tabName]);
    const maybeLoadMoreOnScroll = useCallback(() => {
      if (
        Platform.OS === 'web' ||
        !showSeeMore ||
        loading ||
        loadingMore ||
        loadMoreError ||
        (error && items.length > 0) ||
        autoLoadInFlightRef.current ||
        isCheckingEndReachedRef.current
      ) {
        return;
      }

      const sentinel = endReachedSentinelRef.current;
      if (!sentinel) {
        return;
      }

      isCheckingEndReachedRef.current = true;
      sentinel.measureInWindow((_x, y) => {
        isCheckingEndReachedRef.current = false;

        const threshold = 220;
        const isNearViewportBottom =
          y <= windowHeight + threshold && y >= -threshold;

        if (isNearViewportBottom) {
          autoLoadInFlightRef.current = true;
          void Promise.resolve(loadMore()).finally(() => {
            autoLoadInFlightRef.current = false;
          });
        }
      });
    }, [
      showSeeMore,
      loading,
      loadingMore,
      loadMoreError,
      error,
      items.length,
      windowHeight,
      loadMore,
    ]);

    useImperativeHandle(
      ref,
      () => ({
        maybeLoadMoreOnScroll,
      }),
      [maybeLoadMoreOnScroll],
    );
    const onViewableItemsChanged = useCallback(
      ({
        viewableItems,
      }: {
        viewableItems: {
          item?: RecommendationTrackingItem;
          isViewable?: boolean;
        }[];
      }) => {
        viewableItems.forEach((entry) => {
          if (!entry.isViewable || !entry.item) return;
          const id = entry.item?.lobItemId ?? entry.item?.itemId;
          if (!id || impressedIdsRef.current.has(id)) return;
          impressedIdsRef.current.add(id);
          logEvent(
            RecommendationEvents.Impression,
            trackRecommendationImpression(entry.item),
          );
        });
      },
      [logEvent],
    );

    const handleCardPress = useCallback(
      (item: RecommendationApiItem) => {
        const position =
          items.findIndex((row) => row.lobItemId === item.lobItemId) + 1;
        if (position > 0) {
          logEvent(
            RecommendationEvents.aa_home_recowidget_clicked,
            trackRecommendationCardClick(item, TAB_NAME_FOR_YOU, position),
          );
        }
      },
      [items, logEvent],
    );

    useEffect(() => {
      if (!items.length) return;
      const currentMax = items.length;
      let i = trackedViewIndexRef.current + 1;
      while (i <= currentMax) {
        if (i % TRACKING_PAGE_SIZE === 0) {
          logEvent(
            RecommendationEvents.Home_RecoWidget_Viewed,
            trackRecommendationScrollQuartile(TAB_NAME_FOR_YOU, i),
          );
        }
        i += 1;
      }
      trackedViewIndexRef.current = currentMax;
    }, [items.length, tabName, logEvent]);

    useEffect(() => {
      while (items.length - viewItemLastIndexRef.current >= 2) {
        const start = viewItemLastIndexRef.current;
        const batch = items.slice(start, start + 2);
        logEvent(
          RecommendationEvents.View_Item_List,
          trackRecommendationViewBatch(batch, TAB_NAME_FOR_YOU, start),
        );
        viewItemLastIndexRef.current += 2;
      }
    }, [items, tabName, logEvent]);

    useEffect(() => {
      if (!error) return;
      const code = errorInfo?.code;
      const message = errorInfo?.message ?? 'Unknown error';
      const dedupeKey = `${tabName}|${code}|${message}`;
      if (lastApiErrorLabelRef.current === dedupeKey) return;
      lastApiErrorLabelRef.current = dedupeKey;
      logError(
        RecommendationEvents.Api_Failure,
        trackRecommendationApiFailure(TAB_NAME_FOR_YOU, code, message),
      );
    }, [error, errorInfo, tabName, logError]);

    if (loading && items.length === 0) {
      if (Platform.OS !== 'web') {
        return <ForYouSkeltonLoadingView />;
      }
      return (
        <View style={styles.initialStateContainer}>
          <View style={styles.initialStateContent}>
            <SpinnerLoaderView
              spinnerStyle={SpinnerLoaderViewStyle.SPINNER}
              size={SpinnerLoaderViewSize.LARGE}
            />
          </View>
        </View>
      );
    }

    if (error && items.length === 0)
      return (
        <View style={styles.initialStateContainer}>
          <View style={styles.initialStateContent}>
            <Text style={styles.initialStateMessage}>
              {t('homePage.recommendationsError')}
            </Text>
            <View style={styles.initialStateActionContainer}>
              <ButtonView
                text={t('homePage.retry')}
                type={ButtonViewType.SECONDARY_SMALL}
                onPress={handleRetry}
                disabled={false}
              />
            </View>
          </View>
        </View>
      );

    const renderFooter = () => {
      if (loadingMore)
        return (
          <View style={styles.feedLoaderContainer}>
            <SpinnerLoaderView
              spinnerStyle={SpinnerLoaderViewStyle.SPINNER}
              size={SpinnerLoaderViewSize.SMALL}
            />
          </View>
        );

      if (loadMoreError)
        return (
          <View style={styles.feedLoaderContainer}>
            <Text style={styles.initialStateMessage}>
              {t('homePage.recommendationsError')}
            </Text>
            <View style={styles.initialStateActionContainer}>
              <ButtonView
                text={t('homePage.retry')}
                type={ButtonViewType.SECONDARY_SMALL}
                onPress={retryPagination}
                disabled={false}
              />
            </View>
          </View>
        );

      // "See More" button is only shown on web; mobile uses scroll-based loading via the parent
      if (Platform.OS === 'web' && showSeeMore)
        return (
          <View style={styles.feedLoaderContainer}>
            <ButtonView
              text={t('homePage.seeMore')}
              type={ButtonViewType.SECONDARY_SMALL}
              onPress={handleShowMorePress}
              disabled={false}
            />
          </View>
        );

      return null;
    };

    const renderableItems = items.filter((item) => {
      if (item.lob === 'ad_v3') {
        if (!isAdsRenderEnabled) return false;
        if (failedAdIds.has(item.lobItemId)) return false;
        const adUnit =
          item.data?.primeAdsBanner?.adUnitId ??
          item.data?.primeAdsBanner?.adId;
        if (!adUnit) return false;
        if (Platform.OS !== 'web' && !item.data?.primeAdsBanner?.templateId)
          return false;
        return true;
      }
      return Boolean(mapRecommendationToCardProps(item, onRecommendationPress));
    });

    return (
      <View style={styles.feedContainer}>
        <MasonryGrid
          data={renderableItems}
          renderItem={(item) => {
            if (item.lob === 'ad_v3') {
              return (
                <AdCardView
                  item={item}
                  cardSize={mobileCardSize}
                  onAdFailed={handleAdFailed}
                />
              );
            }
            return (
              <RecommendationCard
                item={item}
                trackClicks={true}
                onTrackClick={handleCardPress}
                onNavigate={onRecommendationPress}
                onPressCta={onPressCta}
                imageRefreshKey={`${imageRefreshKey}:${imageResumeKey}`}
              />
            );
          }}
          ListFooterComponent={
            <View>
              {renderFooter()}
              <View
                ref={endReachedSentinelRef}
                style={styles.endReachedSentinel}
              />
            </View>
          }
          refreshing={refreshing}
          onRefresh={refresh}
          onViewableItemsChanged={onViewableItemsChanged}
        />
      </View>
    );
  },
);

ForYouFeed.displayName = 'ForYouFeed';
export default ForYouFeed;
